
import { useRef, useCallback, useMemo } from 'react';
import { SunStatusUpdaterOptions } from './types';
import { DEFAULT_OPTIONS } from './constants';

export const useRateLimit = (options: SunStatusUpdaterOptions = {}) => {
  const { rateLimit } = { ...DEFAULT_OPTIONS, ...options };
  
  const lastAutoUpdateAttempt = useRef<number>(0);
  const lastManualUpdateAttempt = useRef<number>(0);

  const checkAutoRateLimit = useCallback((): { allowed: boolean; waitMinutes?: number } => {
    const now = Date.now();
    const timeSinceLastAuto = now - lastAutoUpdateAttempt.current;
    const autoRateLimit = rateLimit.autoUpdateMinutes * 60 * 1000;

    if (timeSinceLastAuto < autoRateLimit) {
      const waitMinutes = Math.ceil((autoRateLimit - timeSinceLastAuto) / (1000 * 60));
      return { allowed: false, waitMinutes };
    }

    return { allowed: true };
  }, [rateLimit.autoUpdateMinutes]);

  const checkManualRateLimit = useCallback((): { allowed: boolean; waitMinutes?: number } => {
    const now = Date.now();
    const timeSinceLastManual = now - lastManualUpdateAttempt.current;
    const manualRateLimit = rateLimit.manualUpdateMinutes * 60 * 1000;

    if (timeSinceLastManual < manualRateLimit) {
      const waitMinutes = Math.ceil((manualRateLimit - timeSinceLastManual) / (1000 * 60));
      return { allowed: false, waitMinutes };
    }

    return { allowed: true };
  }, [rateLimit.manualUpdateMinutes]);

  const recordAutoUpdate = useCallback(() => {
    lastAutoUpdateAttempt.current = Date.now();
  }, []);

  const recordManualUpdate = useCallback(() => {
    lastManualUpdateAttempt.current = Date.now();
  }, []);

  const resetRateLimits = useCallback(() => {
    lastAutoUpdateAttempt.current = 0;
    lastManualUpdateAttempt.current = 0;
  }, []);
  const controls = useMemo(
    () => ({
      checkAutoRateLimit,
      checkManualRateLimit,
      recordAutoUpdate,
      recordManualUpdate,
      resetRateLimits
    }),
    [checkAutoRateLimit, checkManualRateLimit, recordAutoUpdate, recordManualUpdate, resetRateLimits]
  );

  return controls;
};
